import React, { useState } from 'react'
import Switch from 'britive-design-system/core/components/switch'
import Tooltip from 'britive-design-system/core/components/tooltip'
import Textfield from 'britive-design-system/core/components/textfield'
import Autocomplete from 'britive-design-system/core/components/autocomplete'
import Table from 'britive-design-system/core/components/table'
import SubmitTableRow from 'static/images/submit-table-row.svg'
import disabledSubmitTableRow from 'static/images/disabled-submit-table-row.svg'
import cancelIcon from 'static/images/cancelIcon.svg'
import EditTableRow from 'static/images/edit-icon.svg'
import clearIcon from 'static/images/close-dark.svg'
import { isEmpty } from 'lodash'

const CONSTRAINT_DRAWER_PERMISSION_TABLE = 'constraint-drawer-permissions'

const ConstraintDrawerTable = ({
  viewOnly,
  tableData,
  systemDefinedValues,
  onTableDataChange,
}) => {
  const [rowToBeEdited, setRowToBeEdited] = useState(-1)
  const [editedValue, setEditedValue] = useState(null)

  const renderSecretValue = row => {
    return isEmpty(row.value) ? '' : row.systemDefined ? row.value : '******'
  }

  const updateEditedValue = rowIndex => {
    setEditedValue(rowIndex >= 0 ? tableData[rowIndex] : null)
  }

  const shouldDisableUpdatingRow = row => {
    return (
      row.mandatory &&
      (editedValue?.value === '' ||
        (editedValue?.systemDefined && editedValue?.value === ''))
    )
  }

  const handleValue = (index, editedValue) => {
    const updatedList = [...tableData]
    updatedList[index] = {
      ...updatedList[index],
      value: editedValue?.value || '',
      systemDefined: editedValue?.systemDefined,
      clearValue: editedValue?.clearValue,
    }
    onTableDataChange(updatedList)
    setEditedValue(null)
  }

  const updateValue = (row, value, systemDefined, clearValue = false) => {
    const updatedRow = {
      ...row,
      value: row.type === 'number' && !systemDefined ? Number(value) : value,
      systemDefined,
      clearValue,
    }
    setEditedValue(updatedRow)
  }

  const tableColumns = [
    {
      width: '20%',
      field: 'name',
      headerName: 'Variable',
      renderColumn: row => (
        <div className={`${CONSTRAINT_DRAWER_PERMISSION_TABLE}-table-cell`}>
          <span>
            {row?.name}{' '}
            {row?.mandatory && !viewOnly && <span style={{ color: 'red' }}>*</span>}
          </span>
        </div>
      ),
    },
    {
      field: 'value',
      headerName: 'Value',
      renderColumn: (row, _, index) => (
        <>
          {index === rowToBeEdited ? (
            <>
              {editedValue?.systemDefined ? (
                <Autocomplete
                  value={[editedValue?.value]}
                  allowClearing={!row?.mandatory}
                  onChange={(_, value) => {
                    updateValue(row, value, editedValue?.systemDefined)
                  }}
                  onInputChange={(event, value) => {
                    if (value === '') {
                      updateValue(row, value, editedValue?.systemDefined, true)
                    }
                  }}
                  options={[...systemDefinedValues]}
                  getOptionLabel={option => option}
                  placeholder={'Select value'}
                  width="100%"
                />
              ) : (
                <Textfield
                  width="100%"
                  type={row.type === 'secret' ? 'password' : row.type}
                  name="value"
                  value={editedValue?.value}
                  icon={row?.mandatory ? null : <img src={clearIcon} />}
                  onChange={e =>
                    updateValue(row, e.target.value, editedValue?.systemDefined)
                  }
                  onIconClick={() => {
                    if (row?.mandatory) return
                    updateValue(row, '', editedValue?.systemDefined, true)
                  }}
                  placeholder="Enter value"
                />
              )}
            </>
          ) : (
            <div className={`${CONSTRAINT_DRAWER_PERMISSION_TABLE}-table-cell`}>
              {row?.type === 'secret' ? renderSecretValue(row) : row?.value}
            </div>
          )}
        </>
      ),
    },
    {
      width: '20%',
      field: 'systemDefined',
      headerName: 'System Defined',
      renderColumn: (row, _, index) => (
        <>
          {index === rowToBeEdited ? (
            <div className={`${CONSTRAINT_DRAWER_PERMISSION_TABLE}-table-cell`}>
              <Switch
                checked={editedValue?.systemDefined}
                onToggle={() => updateValue(row, '', !editedValue?.systemDefined)}
              />
            </div>
          ) : (
            <div className={`${CONSTRAINT_DRAWER_PERMISSION_TABLE}-table-cell`}>
              {row.systemDefined ? 'True' : 'False'}
            </div>
          )}
        </>
      ),
    },
    ...(viewOnly
      ? []
      : [
          {
            headerName: 'Action',
            width: '18%',
            renderColumn: (row, value, index) => (
              <div className={`${CONSTRAINT_DRAWER_PERMISSION_TABLE}-table-cell`}>
                {index === rowToBeEdited ? (
                  <div
                    className={`${CONSTRAINT_DRAWER_PERMISSION_TABLE}-table-tooltip`}
                  >
                    <Tooltip
                      title={
                        shouldDisableUpdatingRow(row) ? 'Please enter value' : 'Add'
                      }
                      position="top"
                    >
                      <img
                        height="25"
                        src={
                          shouldDisableUpdatingRow(row)
                            ? disabledSubmitTableRow
                            : SubmitTableRow
                        }
                        onClick={() => {
                          handleValue(index, editedValue)
                          setRowToBeEdited(-1)
                          updateEditedValue(-1)
                        }}
                      />
                    </Tooltip>
                    <Tooltip title={'Cancel'} position="top">
                      <img
                        src={cancelIcon}
                        onClick={() => {
                          setRowToBeEdited(-1)
                          updateEditedValue(-1)
                        }}
                      />
                    </Tooltip>
                  </div>
                ) : (
                  <Tooltip title="Edit" position="left">
                    <div
                      className={`${CONSTRAINT_DRAWER_PERMISSION_TABLE}-action-button`}
                    >
                      <img
                        src={EditTableRow}
                        width="24"
                        onClick={() => {
                          setRowToBeEdited(index)
                          updateEditedValue(index)
                        }}
                      />
                    </div>
                  </Tooltip>
                )}
              </div>
            ),
          },
        ]),
  ]

  return <Table resizableColumns columns={tableColumns} rows={tableData || []} />
}

export default ConstraintDrawerTable
